import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const KEY_PREFIX = 'sorea-cart-timer:';
// If a saved deadline passed this long ago, treat it as a stale cart and start fresh.
const STALE_AFTER_MS = 30 * 60 * 1000;

const store = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch {}
  },
  remove(key) {
    try { window.localStorage.removeItem(key); } catch {}
  },
  keys() {
    try { return Object.keys(window.localStorage); } catch { return []; }
  },
};

function readConfig() {
  const element = document.getElementById('sorea-cart-timer-config');
  if (!element) return null;
  try {
    const config = JSON.parse(element.textContent || '{}');
    return config.enabled ? config : null;
  } catch {
    return null;
  }
}

function setActive(active) {
  if (active) document.documentElement.dataset.cartTimer = 'on';
  else delete document.documentElement.dataset.cartTimer;
}

/**
 * Countdown bar shown at the top of the cart drawer.
 * Settings come from the "Cart timer" section in the header group.
 */
class CartTimerBar extends HTMLElement {
  #config = null;
  #key = '';
  #deadline = 0;
  #interval = 0;
  #expiring = false;

  connectedCallback() {
    this.#config = readConfig();
    if (!this.#config) {
      setActive(false);
      return;
    }

    this.#key = KEY_PREFIX + (this.dataset.cartToken || 'cart');
    for (const key of store.keys()) {
      if (key.startsWith(KEY_PREFIX) && key !== this.#key) store.remove(key);
    }

    const now = Date.now();
    let deadline = Number(store.get(this.#key));
    if (!deadline || now - deadline > STALE_AFTER_MS) {
      const minutes = Math.max(1, Number(this.#config.minutes) || 5);
      deadline = now + minutes * 60 * 1000;
      store.set(this.#key, String(deadline));
    }
    this.#deadline = deadline;

    setActive(true);
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 500);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  /** Forget the deadline once the cart is emptied, so a new cart starts a new timer. */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (Array.isArray(detail?.items) && detail.items.length === 0) store.remove(this.#key);
      })
      .catch(() => {});
  };

  #tick() {
    const remaining = this.#deadline - Date.now();
    const message = this.querySelector('.cart-timer__msg');
    const time = this.querySelector('.cart-timer__time');

    if (message && message.textContent !== this.#config.message) message.textContent = this.#config.message;

    if (remaining <= 0) {
      if (time) time.textContent = '00:00';
      this.#expire();
      return;
    }

    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    if (time) time.textContent = `${minutes}:${seconds}`;
  }

  async #expire() {
    if (this.#expiring) return;
    this.#expiring = true;
    window.clearInterval(this.#interval);

    if (this.#config.onExpire !== 'empty') {
      setActive(false);
      return;
    }

    store.remove(this.#key);

    const sectionIds = [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter(Boolean)
      ),
    ];
    const lines = Array.from(document.querySelectorAll('#cart-drawer [ref="cartItemRows[]"]'))
      .map((row) => (row instanceof HTMLElement ? { id: row.dataset.key || '', quantity: 0 } : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines, promise: deferred.promise })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { items: data.items || [], itemCount: 0, sections: data.sections, source: 'cart-timer', didError: false },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
    } finally {
      setActive(false);
    }
  }
}

if (!customElements.get('cart-timer-bar')) {
  customElements.define('cart-timer-bar', CartTimerBar);
}